"use client";

import type { SliderApi, SliderOptions } from "@components/composers/slider/Slider";
import type React from "react";

import { Slider, SliderContent, SliderItem } from "@components/composers/slider/Slider";
import { useIsMobile } from "@ui/hooks/useBreakpoint";
import { useInView } from "@ui/lib/useInView";
import { cn } from "@ui/lib/utils";
import { breakpoints } from "@ui/tokens/grid";
import Autoplay from "embla-carousel-autoplay";
import { useEffect, useRef, useState } from "react";

import { FloatingControls } from "./SliderGeneric";

export interface SliderGenericProps {
  animateCenterItem?: boolean;
  className?: string;
  floatingControlPositionClass?: string;
  floatingControls?: boolean;
  items?: React.ReactNode[];
  sliderItemClassName?: string;
  startScrollingAt?: number;
}

const SliderAutoPlay = ({
  items = [],
  className,
  sliderItemClassName,
  animateCenterItem = false,
  floatingControls = false,
  floatingControlPositionClass,
  startScrollingAt = 4,
}: SliderGenericProps) => {
  const displaySlider = items?.length > 0;
  const intersectionRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const [centerItemIndex, setCenterItemIndex] = useState<number | undefined>(undefined);
  const inView = useInView(intersectionRef, { once: true });
  const [api, setApi] = useState<SliderApi>();
  const isMobile = useIsMobile();

  const options: SliderOptions = {
    align: "start",
    active: !!items?.length,
    breakpoints: {
      [`(min-width: ${breakpoints.md} )`]: {
        active: !!items?.length,
      },
      [`(min-width: ${breakpoints.lg} )`]: {
        active: items?.length > startScrollingAt,
      },
    },
  };

  useEffect(() => {
    if (!animateCenterItem) return;
    const onSlidesInView = (api: SliderApi) => {
      const visibleIndexes = api!.slidesInView();
      const centerIndex = isMobile ? visibleIndexes[0] : visibleIndexes[Math.floor(visibleIndexes.length / 2)];
      setCenterItemIndex(centerIndex);
    };
    if (api) onSlidesInView(api);
    api?.on("slidesInView", onSlidesInView);

    return () => {
      api?.off("slidesInView", onSlidesInView);
    };
  }, [api, animateCenterItem, isMobile]);

  return (
    displaySlider && (
      <Slider
        className={cn(className, inView ? "card-reveal" : "")}
        opts={options}
        plugins={[Autoplay({ stopOnMouseEnter: true, stopOnInteraction: false })]}
        ref={intersectionRef}
        setApi={setApi}
      >
        <div
          className={cn(
            "breakout px-outer-gutter !relative overflow-hidden py-4 lg:container lg:breakout-reset lg:!px-0",
          )}
        >
          <SliderContent className='relative'>
            {items?.map((item, index) => (
              <SliderItem
                className={cn(
                  "w-1/2 md:w-1/3 lg:w-1/5",
                  animateCenterItem && "transition-transform duration-500 ease-in-out",
                  animateCenterItem && (index === centerItemIndex ? "lg:scale-100" : "lg:scale-95"),
                  sliderItemClassName,
                )}
                key={index}
              >
                {item}
              </SliderItem>
            ))}
          </SliderContent>
        </div>
        {floatingControls && (
          <FloatingControls imageSelector={floatingControlPositionClass} sliderRef={intersectionRef} />
        )}
      </Slider>
    )
  );
};

SliderAutoPlay.displayName = "SliderAutoPlay";

export { SliderAutoPlay };
